/**
 * FallListe — die Liste der offenen Fälle (linke Spalte von Konzept B).
 *
 * @description
 *   Zeigt die Fälle in zwei Gruppen („Needs a product" vor „Draft ready to
 *   release", die Gruppenordnung ist die Aussage). Jede Zeile ist ein
 *   **Auswahlziel**, kein Aktionsknopf: der Hauptknopf lebt im Detail, das ist
 *   der Kern von Konzept B. Die Zeile trägt Bild, Name, Kennung, Stufe, Zustand
 *   und „Next: …" aus `schritte.js` — dieselbe Ableitung, die das Detail zeigt.
 *
 * @useWhen
 *   - Der Vorschlagsteil braucht die Liste links (Desktop) bzw. die einzige
 *     Fläche (mobil)
 *
 * @dontUseFor
 *   - Eine Tabelle mit Spalten und Kopfzeile — genau die ersetzt dieser Umbau
 *   - Die Bestandstabelle: die bleibt eine Tabelle
 *   - Keine `MobileRecordCard`: die Zeile ist ein AUSWAHLZIEL, keine Karte
 *     (R16-W8) — kein Panel, kein Häkchen, keine Zeilenaktion
 *
 * @related
 *   - `Vorschlagsteil.jsx` — der Abschnitt, der Liste und Detail hält
 *   - `FallDetail.jsx` — die rechte Spalte
 *   - `schritte.js` — die Ableitung „Next: …"
 *
 * @props
 *   zeilen      Die offenen Fälle (`offeneFaelle(bestand)`), in Reihenfolge
 *   gewaehlt   Die Kennung (`sku`) des gewählten Falls oder null
 *   onWaehlen   (zeile) => … — setzt die Auswahl
 *   laufend     { [sku]: art } — welche Zeile gerade arbeitet (nur Anzeige)
 */
import { Box, Stack, Typography } from '@mui/material';
import ProductThumb from '../../components/common/ProductThumb';
import Pill from '../../components/common/Pill';
import { SP, FS, TOUCH_TARGET_COARSE, BUTTON_RADIUS } from '../../theme/tokens';
import { STUFE_TON, ZUSTAND_TON, ZUSTAND_LABEL } from './bestand';
import { schritte } from './schritte';

const GRUPPEN = [
  { state: 'none', titel: 'Needs a product' },
  { state: 'draft', titel: 'Draft ready to release' },
];

/** Die zwei Gruppen — leere Gruppen fallen heraus (kein leerer Gruppenkopf). */
function gruppiere(zeilen) {
  return GRUPPEN
    .map((g) => ({
      ...g,
      eintraege: zeilen.map((z, i) => ({ z, i })).filter(({ z }) => z.state === g.state),
    }))
    .filter((g) => g.eintraege.length > 0);
}

/** „Next: …" — der `kurz` des ersten nicht erledigten Schritts, sonst „Ready".
 *
 * 🔴 **R16-B1:** der Rückfall ist unerreichbar (die Freigabe ist immer
 * mindestens `offen`), er bleibt als Schutz gegen `kurz === null` stehen.
 * Die Gruppe „Draft ready to release" sagt jetzt wirklich „Next: release" —
 * der falsche Text kam aus `schritte.js::freigabe.kurz`, nicht von hier.
 */
function naechstes(zeile) {
  const offen = schritte(zeile, null).find((s) => s.lage !== 'erledigt');
  return offen?.kurz ?? 'Ready to release';
}

export default function FallListe({ zeilen = [], gewaehlt = null, onWaehlen, laufend = null }) {
  const gruppen = gruppiere(zeilen);
  return (
    <Box data-testid="offene-liste" sx={{ px: SP.tight, pb: SP.tight }}>
      {gruppen.map((g) => (
        <Box key={g.state} sx={{ mb: SP.tight }}>
          <Typography variant="subtitle2" sx={{ px: SP.tight, py: SP.chip }}>
            {`${g.titel} (${g.eintraege.length})`}
          </Typography>
          <Stack spacing={SP.tight}>
            {g.eintraege.map(({ z, i }) => {
              const gewaehltHier = gewaehlt === z.sku;
              const laeuft = laufend != null && Object.prototype.hasOwnProperty.call(laufend, z.sku);
              return (
                <Box
                  key={z.sku || i}
                  data-testid={`offene-zeile-${i}`}
                  data-sku={z.sku || undefined}
                  role="button"
                  tabIndex={0}
                  aria-current={gewaehltHier ? 'true' : undefined}
                  aria-busy={laeuft || undefined}
                  onClick={() => onWaehlen?.(z)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onWaehlen?.(z);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: SP.tight,
                    px: SP.tight,
                    py: SP.chip,
                    borderRadius: `${BUTTON_RADIUS}px`,
                    cursor: 'pointer',
                    ...TOUCH_TARGET_COARSE,
                    bgcolor: gewaehltHier ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                  }}
                >
                  <ProductThumb src={z.bild} alt="" size={40} />
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight={600} noWrap title={z.name || undefined}>
                      {z.name || 'no base product'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {z.sku}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {naechstes(z)}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={SP.chip} sx={{ flexShrink: 0 }}>
                    <Pill tone={STUFE_TON[z.grade] || 'plain'}>{z.grade}</Pill>
                    <Pill tone={ZUSTAND_TON[z.state] || 'plain'} nowrap>
                      {ZUSTAND_LABEL[z.state] || z.state}
                    </Pill>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
      {/* Eine leere Liste sagt ihren Grund — nie ein leerer Kasten. */}
      {gruppen.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ px: SP.tight, py: SP.card, fontSize: FS.sm }}>
          Nothing waiting — no item is ready for a product or a release right now.
        </Typography>
      )}
    </Box>
  );
}
